import * as React from 'react'
import { AnimatedSpinnerIcon } from 'primitives/icons/animated-spinner-icon'
import { IconButton } from 'primitives/buttons/icon-button'
import * as Dialog from 'primitives/dialog'
import { tw } from 'utils/classnames'
import { XCloseIcon } from 'primitives/icons'
import { Z_INDEX_MODALS } from 'constants/modals'
import debounce from 'lodash.debounce'
import { localStore } from 'utils/storage'
import { useLocale } from '../../contexts/header-config-context'
import type { PromoPopupType } from './config'
import { PROMO_POPUPS_CONFIG } from './config'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { StartFreeTrialButton } from 'payments/start-free-trial-button'
import { PAYMENT_ENTRY_POINTS } from 'payments/payments-dialog/analytics'

export interface ModalProps {
  promoPopupType: PromoPopupType
  backgroundColor: 'bg-[#00CCCC]' | 'bg-[#291E3E]' | 'bg-[#8559FA]'
  title: string
  description: string
  buttonText: string
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  onClick: () => void
  onClose?: () => void
  isProcessing: boolean
  imageProps: {
    src: string
    srcSet?: string
    alt?: string
  }
  error?: string
  success?: string
  externalUrl?: React.ReactNode
  zIndex?: number
  // GROW-2967 experiment: when set, the unsubscribed popup sells a Studio AI trial.
  // Control leaves these undefined and renders exactly as before.
  subscriptionType?: 'all-access' | 'studio-ai'
  hideImage?: boolean
  microcopy?: string
}

type SubmitEvent = React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>

// Only Enter and Space activate a button; every other key (Tab, arrows, …) must not submit.
function isActivationKey(e: React.KeyboardEvent<HTMLButtonElement>) {
  return e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar'
}

export function Modal({
  promoPopupType,
  isOpen,
  setIsOpen,
  onClick,
  onClose,
  isProcessing,
  backgroundColor,
  imageProps,
  title,
  description,
  buttonText,
  error,
  success,
  externalUrl,
  zIndex = Z_INDEX_MODALS.default,
  subscriptionType,
  hideImage = false,
  microcopy,
}: ModalProps) {
  const locale = useLocale()

  const promoPopupTypeLower = promoPopupType.toLowerCase().replace('_', '-')

  const handleOnClick = React.useCallback(() => {
    sendAmplitudeEvent(AmplitudeEvents.PROMO_POPUP_BUTTON_CLICKED, {
      type: promoPopupTypeLower,
      isKeyboardSubmit: false,
    })
    onClick()
  }, [onClick, promoPopupTypeLower])

  const handleKeyboardSubmit = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      sendAmplitudeEvent(AmplitudeEvents.PROMO_POPUP_BUTTON_CLICKED, {
        type: promoPopupTypeLower,
        isKeyboardSubmit: true,
        keyPressed: e.key,
      })

      onClick()
    },
    [onClick, promoPopupTypeLower],
  )

  const handleSubmission = React.useCallback(
    (e: SubmitEvent) => {
      if (e.type === 'keydown') {
        handleKeyboardSubmit(e as React.KeyboardEvent<HTMLButtonElement>)
      } else {
        handleOnClick()
      }
    },
    [handleOnClick, handleKeyboardSubmit],
  )

  const debouncedSubmitRef = React.useRef(
    debounce((e: SubmitEvent) => {
      handleSubmission(e)
    }, 500),
  )

  const debounceSubmit = React.useCallback((e: SubmitEvent) => {
    debouncedSubmitRef.current(e)
  }, [])

  const handleSubmit = React.useCallback(
    (e: SubmitEvent) => {
      // Ignore non-activation keys (e.g. Tab) so keyboard navigation never submits the CTA.
      if (e.type === 'keydown' && !isActivationKey(e as React.KeyboardEvent<HTMLButtonElement>)) {
        return
      }
      e.preventDefault()
      //if the event is a keydown, then debounce the onClick to avoid multiple submissions
      if (e.type === 'keydown') {
        debounceSubmit(e)
      } else {
        handleSubmission(e)
      }
    },
    [debounceSubmit, handleSubmission],
  )

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen} modal={false} defaultOpen={isOpen}>
      <Dialog.Portal>
        <div data-cf-external>
          <Dialog.Body
            zIndex={zIndex}
            className="backdrop-blur-sm"
            dismissable={false}
            aria-describedby={undefined}
          >
            <Dialog.Title className="sr-only">Promo Popup Dialog</Dialog.Title>
            <div
              className={tw(
                'font-poppins bg-cf-white hover:text-cf-white rounded-2 relative mx-2 flex w-full max-w-[450px] flex-col items-center text-[#2A1F3F] min-[350px]:mx-4',
              )}
            >
              <div
                className={tw(
                  'text-16 text-cf-white w-full whitespace-pre-wrap text-center font-bold transition-all duration-300',
                  error || success ? 'h-fit max-h-[200px]' : 'h-0 max-h-0 opacity-0',
                  error && 'bg-[#E21743] opacity-100',
                  success && 'bg-clover-500 opacity-100',
                )}
              >
                <p className="px-8 py-3">
                  {error || success} {externalUrl}
                </p>
              </div>
              <div
                className={tw(
                  'rounded-2 relative flex flex-col items-center overflow-hidden',
                  backgroundColor,
                )}
              >
                {hideImage ? (
                  <div className="aspect-[4/3] w-[450px] max-w-full" />
                ) : (
                  <img
                    width={450}
                    height={337.5}
                    {...imageProps}
                    alt={imageProps.alt ?? 'Promo Popup Background Image'}
                    className="rounded-b-2 object-cover"
                  />
                )}
                <div
                  className={tw(
                    'text-cf-white absolute bottom-0 left-0 flex h-full w-full flex-col items-center justify-center gap-0 p-4 text-center',
                    locale === 'en' && 'whitespace-pre-wrap',
                    !hideImage &&
                      ['LOGGED_OUT', 'LOGGED_IN_UNSUBSCRIBED'].includes(promoPopupType) &&
                      'pr-[25%]',
                  )}
                >
                  <div
                    className="mb-2 text-[26px] font-bold uppercase leading-none sm:text-[40px]"
                    style={{ fontFamily: 'Basillion, sans-serif' }}
                  >
                    {title}
                  </div>
                  <div className="text-16 mb-4 whitespace-pre-wrap">{description}</div>
                  {promoPopupType === 'LOGGED_IN_UNSUBSCRIBED' ? (
                    <>
                      <StartFreeTrialButton
                        className={tw(
                          'text-cf-white rounded-2 text-16 hover:shadow-cf-elevation-light-1 flex min-w-[170px] flex-row justify-center gap-2 bg-[#FD647E] px-4 py-2 font-bold uppercase',
                          'disabled:cursor-not-allowed disabled:opacity-70',
                        )}
                        entryPoint={PAYMENT_ENTRY_POINTS.PROMO_POPUP}
                        label={buttonText}
                        subscriptionType={subscriptionType}
                        onClick={handleOnClick}
                      />
                      {microcopy ? (
                        <div className="text-12 mt-2 font-semibold">{microcopy}</div>
                      ) : null}
                    </>
                  ) : (
                    // oxlint-disable-next-line react/button-has-type
                    <button
                      className={tw(
                        'text-cf-white rounded-2 text-16 hover:shadow-cf-elevation-light-1 flex min-w-[170px] flex-row justify-center gap-2 bg-[#FD647E] px-4 py-2 font-bold uppercase',
                        'disabled:cursor-not-allowed disabled:opacity-70',
                      )}
                      onKeyDown={handleSubmit}
                      onClick={handleSubmit}
                      disabled={
                        isProcessing || (promoPopupType !== 'LOGGED_IN_YEARLY' && Boolean(success))
                      }
                    >
                      {isProcessing ? <AnimatedSpinnerIcon /> : null} {buttonText}
                    </button>
                  )}
                </div>
              </div>
              <div className="absolute right-[-4px] top-[-4px]">
                <Dialog.Close
                  asChild
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      localStore.setString(
                        PROMO_POPUPS_CONFIG[promoPopupType].CLOSE_KEY,
                        Date.now().toString(),
                      )
                    }
                    onClose?.()
                  }}
                >
                  <IconButton
                    aria-label="Close dialog"
                    variant="violet"
                    className={tw(
                      'hover:bg-aubergine-500 rounded-full bg-[#493675] p-[5px] transition-transform',
                      backgroundColor === 'bg-[#291E3E]' && 'bg-cf-lavender hover:bg-cf-lavender',
                    )}
                  >
                    <XCloseIcon
                      className={tw(
                        'h-[26px] w-[26px]',
                        backgroundColor === 'bg-[#291E3E]' ? 'text-[#291E3E]' : 'text-cf-lavender',
                      )}
                    />
                  </IconButton>
                </Dialog.Close>
              </div>
            </div>
          </Dialog.Body>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
